/**
 * Detect docx templates whose FIRST page prints without a header or
 * footer — i.e. Word would render page 1 blank in those bands.
 *
 * Why this exists: `@eigenpal/docx-editor-react` renders header/footer
 * globally (one set, applied flat to every page). It has no API for
 * "show header on pages 2+ only", so a template like the Dr Sam Jones
 * letterhead — whose first sectPr deliberately omits
 * `<w:headerReference>` so page 1 prints clean — has the page-2 banner
 * drawn on page 1 too in the editor view.
 *
 * `firstPagePrintsWithoutHeaderOrFooter` returns true exactly when
 * Word would print page 1 with neither a header nor a footer. Callers
 * (the Report Builder editor wrapper) set a marker attribute so a CSS
 * rule can hide eigenpal's header/footer slots on the first rendered
 * page only — the closest approximation to Word's per-section
 * behaviour we can get without forking the editor.
 *
 * Detection walks the FIRST sectPr in document order and asks "does
 * page 1 of this section have a header/footer in print?". The shape
 * Word uses is:
 *
 * - If the sectPr has `<w:titlePg/>` (the explicit "first page is
 *   different" marker), page 1 uses `w:type="first"` references —
 *   so page 1 has a header iff `headerReference[w:type="first"]`
 *   exists, and likewise for footer.
 *
 * - If the sectPr has no `<w:titlePg/>`, page 1 uses `w:type="default"`
 *   (or unattributed) references — so page 1 has a header iff
 *   `headerReference[w:type="default"]` (or untyped) exists.
 *
 * We hide editor h/f on page 1 only when BOTH page-1 header AND
 * page-1 footer are absent in print. The "header present but footer
 * absent" (or vice versa) shape is uncommon and would need a more
 * granular CSS rule; treating it as "don't hide" keeps the rule
 * simple and avoids hiding genuine page-1 chrome.
 */
import JSZip from "jszip";

// Captures the first `<w:sectPr>...</w:sectPr>` (or self-closing form).
// Matching the FIRST sectPr in document order is intentional: that's
// the section that contains the editor's first rendered page in
// practice. Subsequent sections drive pages 2+ via the editor's own
// pagination.
const SECTPR_BLOCK_PATTERN = /<w:sectPr\b(?:[^>]*?\/>|[^>]*?>[\s\S]*?<\/w:sectPr>)/;
const TITLEPG_PATTERN = /<w:titlePg\b/;
// Match each self-closing `<w:headerReference .../>` or
// `<w:footerReference .../>` and capture the full element so we can
// inspect its `w:type` attribute. Self-closing only because OOXML
// emits these as self-closing in every shape we've seen; the
// expanded form `<w:headerReference ...></w:headerReference>` would
// not occur for these reference elements (they carry no children).
const HEADER_REF_PATTERN = /<w:headerReference\b[^>]*?\/>/g;
const FOOTER_REF_PATTERN = /<w:footerReference\b[^>]*?\/>/g;
const TYPE_ATTR_PATTERN = /\bw:type="([^"]*)"/;

const refOfType = (
  block: string,
  pattern: RegExp,
  wantedType: "default" | "first"
): boolean => {
  for (const ref of block.match(pattern) ?? []) {
    const typeMatch = ref.match(TYPE_ATTR_PATTERN);
    // Unattributed reference defaults to w:type="default" per OOXML.
    const actualType = typeMatch ? typeMatch[1] : "default";
    if (actualType === wantedType) {
      return true;
    }
  }
  return false;
};

/** Inspect a `word/document.xml` string. Returns true when Word would
 *  print page 1 with neither a header nor a footer. */
export const firstPagePrintsWithoutHeaderOrFooter = (
  documentXml: string
): boolean => {
  const firstSectPrMatch = documentXml.match(SECTPR_BLOCK_PATTERN);
  if (!firstSectPrMatch) {
    return false;
  }
  const firstSectPr = firstSectPrMatch[0];
  const hasTitlePg = TITLEPG_PATTERN.test(firstSectPr);
  // With titlePg: page 1 uses w:type="first" refs.
  // Without:     page 1 uses w:type="default" (or unattributed) refs.
  const wantedType = hasTitlePg ? "first" : "default";
  const page1HasHeader = refOfType(firstSectPr, HEADER_REF_PATTERN, wantedType);
  const page1HasFooter = refOfType(firstSectPr, FOOTER_REF_PATTERN, wantedType);

  return !page1HasHeader && !page1HasFooter;
};

/** Parse the docx bytes and apply the page-1 print check.
 *
 *  Returns false on any parse / read failure — the editor renders fine
 *  without the hint, so we'd rather pass through than show a broken
 *  state because of a corrupt docx.
 */
export const docxBytesShowEmptyFirstPageInPrint = async (
  docxBytes: ArrayBuffer | Uint8Array
): Promise<boolean> => {
  try {
    const zip = await JSZip.loadAsync(docxBytes);
    const documentXml = await zip.file("word/document.xml")?.async("string");
    if (!documentXml) {
      return false;
    }
    return firstPagePrintsWithoutHeaderOrFooter(documentXml);
  } catch {
    return false;
  }
};
